import { IconProps } from '@chakra-ui/icon'
import { t } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import iconVerticalDotsLightBlue from '~/assets/icons/icon-vertical-dots-lightblue.png'
import IconBlocked from '~/assets/svgs/icon-blocked.svg?react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconCloseX from '~/assets/svgs/icon-close-x.svg?react'
import IconEditPencil from '~/assets/svgs/icon-edit-pencil.svg?react'
import IconFax from '~/assets/svgs/icon-fax.svg?react'
import IconLink from '~/assets/svgs/icon-link.svg?react'
import IconMessaging from '~/assets/svgs/icon-messaging.svg?react'
import IconMove from '~/assets/svgs/icon-move.svg?react'
import IconResend from '~/assets/svgs/icon-resend.svg?react'
import IconTrash from '~/assets/svgs/icon-trash.svg?react'
import IconUnreadMessage from '~/assets/svgs/icon-unread-message.svg?react'
import {
  CopyLinkToClipboardFabIcon,
  CopyLinkToClipboardFabIconProps,
} from '~/components/compositional/QuickActionIcons/CopyLinkToClipboardFabIcon'
import { ImageButton } from '~/components/core'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import { colors } from '~/styles'

type MarkAsUnreadProps = {
  action: 'mark_read' | 'mark_unread'
  onClick: () => void
  size?: QuickActionButtonSizes
}

interface QuickActionProps {
  size?: QuickActionButtonSizes
  iconProps?: IconProps
  onClick?: () => void
  tooltipText?: string
  tooltipDisabled?: boolean
}

export const Edit = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconEditPencil}
      aria-label={t('voicemail.tooltip.editContact')}
      tooltipText={t('voicemail.tooltip.editContact')}
      onClick={onClick}
    />
  )
}

export const CopyLinkToClipboard = (
  props: Omit<CopyLinkToClipboardFabIconProps, 'icon'>,
) => <CopyLinkToClipboardFabIcon {...props} icon={IconLink} />

export const NewCall = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconCallStart}
      aria-label={t('voicemail.tooltip.newCall')}
      tooltipText={t('voicemail.tooltip.newCall')}
      onClick={onClick}
    />
  )
}

export const Delete = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconTrash}
      aria-label={t('voicemail.tooltip.delete')}
      tooltipText={t('voicemail.tooltip.delete')}
      onClick={onClick}
    />
  )
}

export const ResendFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconResend}
      aria-label={t('voicemail.tooltip.resendFax')}
      tooltipText={t('voicemail.tooltip.resendFax')}
      onClick={onClick}
    />
  )
}

export const MoveFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconMove}
      aria-label={t('voicemail.tooltip.moveFax')}
      tooltipText={t('voicemail.tooltip.moveFax')}
      onClick={onClick}
    />
  )
}

export const NewFax = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconFax}
      aria-label={t('voicemail.tooltip.newFax')}
      tooltipText={t('voicemail.tooltip.newFax')}
      onClick={onClick}
    />
  )
}

export const NewText = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconMessaging}
      aria-label={t('voicemail.tooltip.newText')}
      tooltipText={t('voicemail.tooltip.newText')}
      onClick={onClick}
    />
  )
}

export const MarkAsUnread = ({ action, onClick, size }: MarkAsUnreadProps) => {
  const { t } = useTranslation()
  const text =
    action === 'mark_read'
      ? t('voicemail.tooltip.markAsRead')
      : t('voicemail.tooltip.markAsUnread')
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconUnreadMessage}
      aria-label={text}
      tooltipText={text}
      onClick={onClick}
    />
  )
}

export const Settings = ({ onClick }: QuickActionProps) => (
  <ImageButton
    width="24px"
    imgWidth={6}
    imgHeight={18}
    backColor={colors.transparent}
    activeColor={colors.transparent}
    hoveredColor={colors.transparent}
    icon={iconVerticalDotsLightBlue}
    onClick={onClick}
  />
)

export const BlockNumber = ({ size, onClick }: QuickActionProps) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={QuickActionButtonVariants.Default_200}
      size={size}
      actionIcon={IconBlocked}
      aria-label={t('voicemail.tooltip.blockNumber')}
      tooltipText={t('voicemail.tooltip.blockNumber')}
      onClick={onClick}
    />
  )
}

export const UnblockNumber = ({
  size,
  variant,
  onClick,
}: QuickActionProps & { variant?: QuickActionButtonVariants }) => {
  const { t } = useTranslation()
  return (
    <QuickActionButton
      variant={variant ?? QuickActionButtonVariants.Destructive_200}
      size={size}
      actionIcon={IconBlocked}
      aria-label={t('voicemail.tooltip.unblockNumber')}
      tooltipText={t('voicemail.tooltip.unblockNumber')}
      onClick={onClick}
    />
  )
}

export const Close = ({
  size,
  iconProps,
  onClick,
  tooltipDisabled = false,
  tooltipText = t('voicemail.tooltip.close'),
}: QuickActionProps) => (
  <QuickActionButton
    variant={QuickActionButtonVariants.Default_100}
    size={size}
    iconProps={iconProps}
    actionIcon={IconCloseX}
    aria-label={tooltipText}
    tooltipText={tooltipText}
    onClick={onClick}
    tooltipDisabled={tooltipDisabled}
  />
)
